import React, { useState, useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { PARTNER_FILE_BULK_LIST_QUERY, PARTNER_FILE_BULK_PAGED_LIST_QUERY } from '../gql/queries';
import PartnerFileBulkFormCard from './PartnerFileBulkFormCard';
import { useToast } from '@totalsoft/rocket-ui';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { useMutation, useSubscription } from '@apollo/client';
import { GENERATE_BULK_PARTNER_FILES_MUTATION } from '../gql/mutations';
import { useSelector } from 'react-redux';
import { selectUserId } from 'selectors/userInfoSelectors';
import { useTranslation } from 'react-i18next';
import { getDateValue } from 'utils/functions';
import { GENERATE_PARTNER_FILES_EXECUTED } from '../gql/subscriptions';

const defaultPartnerFileBulkFilters = {
  startDate: {
    value: null,
    isValid: true
  },
  endDate: {
    value: null,
    isValid: true
  }
};
const defaultPager = { page: 1, pageSize: 10 };

function PartnerFileBulkContainer() {
  const [filters, setFilters] = useState(defaultPartnerFileBulkFilters);
  const [allPartnersSelected, setAllPartnersSelected] = useState(false);
  const [anyPartnerSelected, setAnyPartnerSelected] = useState(false);
  const [shouldGetAllPartners, setShouldGetAllPartners] = useState(false);
  const [partners, setPartners] = useState([]);
  const [pagedPartners, setPagedPartners] = useState([]);
  const [pager, setPager] = useState(defaultPager);
  const [totalCount, setTotalCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const userId = useSelector(state => selectUserId(state));
  const { t } = useTranslation();
  const addToast = useToast();
  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
    }
  });

  const { refetch } = useQueryWithErrorHandling(PARTNER_FILE_BULK_PAGED_LIST_QUERY, {
    variables: {
      pager: {
        size: pager.pageSize,
        page: pager.page
      },
      filters: {
        startDate: getDateValue(filters.startDate.value),
        endDate: getDateValue(filters.endDate.value)
      }
    },
    fetchPolicy: 'network-only',
    skip: shouldGetAllPartners || !filters.startDate.isValid || !filters.endDate.isValid || saving,
    onCompleted: data => {
      setTotalCount(data.partnerFileBulkPagedList.pagination.totalCount);
      const newPartners = [...partners];
      const newPagedPartners = [];
      data.partnerFileBulkPagedList.values.forEach(value => {
        const currentPartner = newPartners.find(partner => partner.partnerId === value.partnerId);
        if (!currentPartner) {
          newPartners.push({ ...value, isSelected: false });
          newPagedPartners.push({ ...value, isSelected: false });
        } else newPagedPartners.push(currentPartner);
      });
      setPartners(newPartners);
      setPagedPartners(newPagedPartners);
    }
  });

  useQueryWithErrorHandling(PARTNER_FILE_BULK_LIST_QUERY, {
    variables: {
      filters: {
        startDate: getDateValue(filters.startDate.value),
        endDate: getDateValue(filters.endDate.value)
      }
    },
    fetchPolicy: 'network-only',
    skip: !shouldGetAllPartners,
    onCompleted: data => {
      setPartners(data.partnerFileBulkList.map(value => ({ ...value, isSelected: true })));
      setShouldGetAllPartners(false);
    }
  });

  const [generateBulkPartnerFiles] = useMutation(GENERATE_BULK_PARTNER_FILES_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data;
      storeKey({ correlationId });
    }
  });

  useSubscription(GENERATE_PARTNER_FILES_EXECUTED, {
    variables: {
      userId
    },
    onSubscriptionData: ({
      subscriptionData: {
        data: {
          generatePartnerFilesExecuted: { unprocessedPartners }
        }
      }
    }) => {
      if (unprocessedPartners) addToast(t('BulkPartners.PartnerFilesNotGenerated', { unprocessedPartners }), 'error');
      else addToast(t('BulkPartners.PartnerFilesGenerated'), 'success');
      setPager(prevState => {
        return { ...prevState, page: 1 };
      });
      setPartners([]);
      setSaving(false);
      refetch();
    }
  });

  useEffect(() => {
    if (filters.startDate.isValid && filters.endDate.isValid)
      setPager(prevState => {
        return { ...prevState, page: 1 };
      });
  }, [filters]);

  useEffect(() => {
    setAllPartnersSelected(partners.length > 0 ? partners.every(partner => partner.isSelected) : false);
    setAnyPartnerSelected(partners.some(partner => partner.isSelected));
  }, [partners]);

  const onSelectAllPartners = event => {
    setShouldGetAllPartners(!allPartnersSelected);
    setPartners(prevState =>
      prevState.map(partner => {
        return { ...partner, isSelected: event.target.checked };
      })
    );
    setPagedPartners(prevState =>
      prevState.map(partner => {
        return { ...partner, isSelected: event.target.checked };
      })
    );
  };

  const onCheckBoxChange = partnerId => () => {
    setPartners(prevState => {
      return prevState.map(partner => {
        if (partner.partnerId === partnerId) return { ...partner, isSelected: !partner.isSelected };
        return partner;
      });
    });
    setPagedPartners(prevState => {
      return prevState.map(partner => {
        if (partner.partnerId === partnerId) return { ...partner, isSelected: !partner.isSelected };
        return partner;
      });
    });
  };

  const onFilterChange = useCallback(
    prop => value => {
      const isValid = !value || (Date.parse(value) && new Date(value) > new Date('01/01/1900'));
      const updatedFilters = { ...filters, [prop]: { value: value, isValid: isValid } };
      setFilters(updatedFilters);
      if (isValid) setPartners([]);
    },
    [filters]
  );

  const onAddBulkFile = useCallback(() => {
    setSaving(true);
    const selectedPartners = partners.filter(partner => partner.isSelected);
    generateBulkPartnerFiles({
      variables: {
        input: {
          partners: [
            ...selectedPartners.map(partner => ({
              partnerId: partner.partnerId,
              partnerTypeId: partner.partnerTypeId,
              uniqueId: partner.uniqueId,
              partnerName: partner.partnerName
            }))
          ]
        }
      }
    });
  }, [generateBulkPartnerFiles, partners]);

  return (
    <Box px={2}>
      <PartnerFileBulkFormCard
        partnerFileBulkList={pagedPartners}
        filters={filters}
        onFilterChange={onFilterChange}
        allPartnersSelected={allPartnersSelected}
        anyPartnerSelected={anyPartnerSelected}
        onSelectAllPartners={onSelectAllPartners}
        pager={pager}
        setPager={setPager}
        totalCount={totalCount}
        setPartners={setPartners}
        onCheckBoxChange={onCheckBoxChange}
        onAddBulkFile={onAddBulkFile}
        saving={saving}
      />
    </Box>
  );
}

export default PartnerFileBulkContainer;
